import type { TStevensModelData, TSubCategory, TTopLevelCategory } from "components/pages/PageConfiguration/layout/CompetitorsModelNumbers/layout/CompetitorsCollapse/layout/CompetitorCategoryTable/competitorsTable.builder"

/**
 * The endpoint returns either a flat list of rows for a category ("Without category")
 * or a nested superCategory -> topLevelCategory -> subCategory -> rows structure.
 */
export type TCompetitorsModelNumbers = Record<string, TStevensModelData[] | TTopLevelCategory>

export type TCompetitorsSearchResult = {
	/** Same shape as the input, with non-matching rows and now-empty branches removed. */
	data: TCompetitorsModelNumbers
	/** Rows kept. */
	matches: number
	/** Keys of the top-level panels that still hold rows, for auto-expanding them. */
	matchedTopLevelKeys: string[]
}

/** A row matches on its Stevens model number or on any competitor's model numbers. */
const rowMatches = (row: TStevensModelData, term: string): boolean => {
	if (row.stevens_model_number?.toLowerCase().includes(term)) return true

	return (row.competitors || []).some((competitor) =>
		(competitor.competitor_model_numbers || []).some((modelNumber) => modelNumber.toLowerCase().includes(term)),
	)
}

const filterRows = (rows: TStevensModelData[], term: string): TStevensModelData[] =>
	rows.filter((row) => rowMatches(row, term))

/** Drops empty sub-categories so a category with no remaining rows disappears entirely. */
const filterSubCategories = (subCategories: TSubCategory, term: string): TSubCategory => {
	const kept: TSubCategory = {}

	Object.entries(subCategories).forEach(([name, rows]) => {
		const matching = filterRows(rows, term)
		if (matching.length) kept[name] = matching
	})

	return kept
}

const filterTopLevel = (topLevel: TTopLevelCategory, term: string): TTopLevelCategory => {
	const kept: TTopLevelCategory = {}

	Object.entries(topLevel).forEach(([name, subCategories]) => {
		const matching = filterSubCategories(subCategories, term)
		if (Object.keys(matching).length) kept[name] = matching
	})

	return kept
}

const countRows = (branch: TStevensModelData[] | TTopLevelCategory): number => {
	if (Array.isArray(branch)) return branch.length

	return Object.values(branch).reduce<number>(
		(total, subCategories) =>
			total + Object.values(subCategories).reduce<number>((sum, rows) => sum + rows.length, 0),
		0,
	)
}

/** Total rows across the whole payload, for the "N of M" hint next to the search box. */
export const countCompetitorsModelNumbers = (data?: TCompetitorsModelNumbers): number =>
	Object.values(data || {}).reduce<number>((total, branch) => total + countRows(branch), 0)

/**
 * Narrows the payload to the rows matching `term`, keeping its shape so the collapse and
 * table builders need no knowledge of searching.
 *
 * Filtering the data rather than the rendered rows matters now the big category is
 * virtualized: only ~11 rows are in the DOM, so the browser's own Ctrl+F can no longer
 * find a model number. This restores that ability.
 *
 * An empty or whitespace-only term returns the input untouched.
 */
export const filterCompetitorsModelNumbers = (
	data: TCompetitorsModelNumbers | undefined,
	term: string,
): TCompetitorsSearchResult => {
	const normalized = term.trim().toLowerCase()

	if (!data || !normalized) {
		return { data: data || {}, matches: countCompetitorsModelNumbers(data), matchedTopLevelKeys: [] }
	}

	const filtered: TCompetitorsModelNumbers = {}

	Object.entries(data).forEach(([topLevelKey, branch]) => {
		const kept = Array.isArray(branch)
			? filterRows(branch, normalized)
			: filterTopLevel(branch as TTopLevelCategory, normalized)

		const isEmpty = Array.isArray(kept) ? !kept.length : !Object.keys(kept).length
		if (!isEmpty) filtered[topLevelKey] = kept
	})

	return {
		data: filtered,
		matches: countCompetitorsModelNumbers(filtered),
		matchedTopLevelKeys: Object.keys(filtered),
	}
}
